import React, { useMemo, useState } from 'react';
import { Loader2, X, UserCog, Repeat } from 'lucide-react';
import type { Profile, CobrancaAcaoEtapa } from '../../../types';
import PersonPicker from '../../../src/components/PersonPicker';
import type { ResponsaveisPorEtapa as Mapa } from '../../../src/services/api';
import { ETAPAS_MANUAIS, ORDEM_ETAPAS_MANUAIS } from './etapas';

interface Props {
    mapa: Mapa;
    perfis: Profile[];
    salvando: CobrancaAcaoEtapa | null;
    onChange: (etapa: CobrancaAcaoEtapa, pool: string[]) => void;
}

/**
 * Quem recebe as etapas novas, por etapa.
 *
 * É por etapa porque as funções são diferentes: D+10 e D+24 são contato de
 * cobrança (Dhaiane e João Henrique), D+17 é documentação jurídica (Bruna).
 * Antes os dois primeiros caíam na Bruna e ela tinha que devolver o chamado.
 *
 * Com duas ou mais pessoas na etapa, o banco alterna em rodízio a cada caso
 * novo — não distribui por quem tem menos fila. Isso é deliberado: distribuir
 * por menor carga foi tentado nos leads e vira "empurra tudo pra quem está
 * atrás até empatar".
 */
const ResponsaveisPorEtapa: React.FC<Props> = ({ mapa, perfis, salvando, onChange }) => {
    const [abertoEm, setAbertoEm] = useState<CobrancaAcaoEtapa | null>(null);

    const nomePorId = useMemo(
        () => new Map(perfis.map(p => [String(p.id), p.full_name])),
        [perfis]
    );

    const adicionar = (etapa: CobrancaAcaoEtapa, id: string) => {
        if (!id || mapa[etapa].includes(id)) return;
        onChange(etapa, [...mapa[etapa], id]);
        setAbertoEm(null);
    };

    const remover = (etapa: CobrancaAcaoEtapa, id: string) => {
        const pool = mapa[etapa].filter(x => x !== id);
        // pool vazio deixaria a etapa sem dono e sem aviso — o cron grava null
        if (!pool.length) return;
        onChange(etapa, pool);
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <UserCog size={18} />
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Quem recebe cada etapa</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Com duas ou mais pessoas, o sistema alterna entre elas a cada caso novo.
                        No contato (D+4 e D+10), cliente que já tem dono na carteira continua com ele.
                        Notificação (D+17) e negativação (D+24) ficam com quem está na etapa — e quem
                        está no D+24 recebe os clientes marcados para negativar.
                    </p>
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {ORDEM_ETAPAS_MANUAIS.map(etapa => {
                    const meta = ETAPAS_MANUAIS[etapa];
                    const pool = mapa[etapa] ?? [];
                    return (
                        <div
                            key={etapa}
                            className={`rounded-lg border-l-4 ${meta.accent} bg-gray-50 dark:bg-gray-700/40 p-3 flex flex-col gap-2`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${meta.badge}`}>
                                    {etapa}
                                </span>
                                {pool.length > 1 ? (
                                    <span
                                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-400"
                                        title="Alterna entre as pessoas a cada caso novo"
                                    >
                                        <Repeat size={11} /> rodízio
                                    </span>
                                ) : null}
                                {salvando === etapa ? <Loader2 size={13} className="animate-spin text-gray-400" /> : null}
                            </div>

                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{meta.titulo}</p>

                            <div className="flex flex-wrap gap-1.5">
                                {pool.map(id => (
                                    <span
                                        key={id}
                                        className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[11px] font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                                    >
                                        {nomePorId.get(id) ?? 'Desconhecido'}
                                        {pool.length > 1 ? (
                                            <button
                                                onClick={() => remover(etapa, id)}
                                                title="Tirar desta etapa"
                                                className="p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                            >
                                                <X size={11} />
                                            </button>
                                        ) : null}
                                    </span>
                                ))}
                            </div>

                            {abertoEm === etapa ? (
                                <PersonPicker
                                    value=""
                                    onChange={(id) => adicionar(etapa, id)}
                                    profiles={perfis.filter(p => !pool.includes(String(p.id)))}
                                    currentUserProfile={null}
                                    defaultLabel="Escolher pessoa"
                                />
                            ) : (
                                <button
                                    onClick={() => setAbertoEm(etapa)}
                                    className="self-start text-[11px] font-bold text-primary hover:underline"
                                >
                                    + Adicionar pessoa
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ResponsaveisPorEtapa;
